// controls/address.js — champ complexe "Adresse postale"
import React, { useState, useEffect } from "react";
import CommonControls from "./inc/Common";
import { BiHome } from "react-icons/bi";
import { debug } from './../../../../utils'

/**
 * Création du champ complexe "Adresse postale"
 */
export function createAddressField() {
  return {
    type: "object",
    title: "Adresse postale",
    "x-ui": "address",
    properties: {
      includeCountry: true,
      labels: {
        civility: "Civilité",
        firstname: "Prénom",
        name: "Nom",
        street: "Adresse",
        postalCode: "Code postal",
        city: "Ville",
        country: "Pays",
      },
    },
  };
}

/**
 * Éditeur du champ complexe "Adresse postale"
 */
export function EditAddressField({ field, onChange }) {
  const [includeCountry, setIncludeCountry] = useState(
    field.properties?.includeCountry ?? true
  );
  const [labels, setLabels] = useState(
    field.properties?.labels || {
      civility: "Civilité",
      firstname: "Prénom",
      name: "Nom",
      street: "Adresse",
      postalCode: "Code postal",
      city: "Ville",
      country: "Pays",
    }
  );

  // 🔄 synchronisation vers le parent
  useEffect(() => {
    const updated = {
      ...field,
      properties: { includeCountry, labels },
    };
    onChange(updated);
  }, [includeCountry, labels]);

  const updateLabel = (key, value) =>
    setLabels((prev) => ({ ...prev, [key]: value }));

  return (
    <>
      <CommonControls field={field} onChange={onChange} />

      <div className="mb-3">
        <label className="form-label fw-bold d-flex align-items-center gap-2">
          <BiHome size={18} /> Configuration du champ "Adresse postale"
        </label>

        {/* Inclure le champ pays */}
        <div className="form-check form-switch mb-3">
          <input
            className="form-check-input"
            type="checkbox"
            id="includeCountry"
            checked={includeCountry}
            onChange={(e) => setIncludeCountry(e.target.checked)}
          />
          <label className="form-check-label" htmlFor="includeCountry">
            Inclure le champ Pays
          </label>
        </div>

        {/* Libellés */}
        <div>
          {Object.entries(labels).map(([key, val]) =>
            (key !== "country" || includeCountry) && (
              <div className="mb-2" key={key}>
                <label className="form-label text-capitalize">
                  Libellé {key}
                </label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={val}
                  onChange={(e) => updateLabel(key, e.target.value)}
                />
              </div>
            )
          )}
        </div>
      </div>
    </>
  );
}

export const editAddressField = (props) => <EditAddressField {...props} />;

/* =========================================================
   🧩 FRONT — rendu du champ complexe "Adresse postale"
   ========================================================= */
export const RenderAddressField = ({
  field,
  value = {},
  onChange,
  className,
  errors = {}
}) => {

  const props = field.properties || {};
  const includeCountry = props.includeCountry ?? true;

  const labels = props.labels || {
    civility: "Civilité",
    firstname: "Prénom",
    name: "Nom",
    street: "Adresse",
    postalCode: "Code postal",
    city: "Ville",
    country: "Pays",
  };

  const {
    civility = "",
    firstname = "",
    name = "",
    street = "",
    postalCode = "",
    city = "",
    country = ""
  } = value;

  const handleChange = (key, val) => {
    onChange?.({
      civility,
      firstname,
      name,
      street,
      postalCode,
      city,
      country,
      [key]: val,
    });
  };

  return (
    <div className={`fb-field fb-address ${field["x-bloc-class"] || ""} ${className || ""}`}>

      {field.title && (
        <label className="form-label fw-bold d-block mb-2">
          {field.title}
        </label>
      )}

      {/* LIGNE 1 */}
      <div className="row g-2">

        {/* Civilité */}
        <div className="col-md-2">
          <label className="form-label">{labels.civility}</label>
          <select
            className={`form-select form-select-sm ${errors?.civility ? "is-invalid" : ""
              }`}
            value={civility}
            onChange={(e) => handleChange("civility", e.target.value)}
          >
            <option value="">—</option>
            <option value="Mme">Mme</option>
            <option value="M.">M.</option>
          </select>
        </div>

        {/* Prénom */}
        <div className="col-md-5">
          <label className="form-label">{labels.firstname}</label>
          <input
            type="text"
            className={`form-control form-control-sm ${errors?.firstname ? "is-invalid" : ""
              }`}
            value={firstname}
            onChange={(e) => handleChange("firstname", e.target.value)}
          />
        </div>

        {/* Nom */}
        <div className="col-md-5">
          <label className="form-label">{labels.name}</label>
          <input
            type="text"
            className={`form-control form-control-sm ${errors?.name ? "is-invalid" : ""
              }`}
            value={name}
            onChange={(e) => handleChange("name", e.target.value)}
          />
        </div>
      </div>

      {/* Adresse */}
      <div className="row g-2 mt-2">
        <div className="col-12">
          <label className="form-label">{labels.street}</label>
          <input
            type="text"
            className={`form-control form-control-sm ${errors?.street ? "is-invalid" : ""
              }`}
            value={street}
            onChange={(e) => handleChange("street", e.target.value)}
          />
        </div>
      </div>

      {/* Code postal / Ville / Pays */}
      <div className="row g-2 mt-2">
        <div className="col-md-3">
          <label className="form-label">{labels.postalCode}</label>
          <input
            type="text"
            className={`form-control form-control-sm ${errors?.postalCode ? "is-invalid" : ""
              }`}
            value={postalCode}
            onChange={(e) => handleChange("postalCode", e.target.value)}
          />
        </div>

        <div className="col-md-5">
          <label className="form-label">{labels.city}</label>
          <input
            type="text"
            className={`form-control form-control-sm ${errors?.city ? "is-invalid" : ""
              }`}
            value={city}
            onChange={(e) => handleChange("city", e.target.value)}
          />
        </div>

        {includeCountry && (
          <div className="col-md-4">
            <label className="form-label">{labels.country}</label>
            <input
              type="text"
              className={`form-control form-control-sm ${errors?.country ? "is-invalid" : ""
                }`}
              value={country}
              onChange={(e) => handleChange("country", e.target.value)}
            />
          </div>
        )}
      </div>

    </div>
  );
};
